import { translate } from 'i18n/i18n'
import { isNil } from 'lodash'
import type { TFlowRateUnit, TVolumeUnit } from 'stores/userDetailsStore/userDetailsStore'
import { userDetailsStore } from 'stores/userDetailsStore/userDetailsStore'
import type { TUnitConverterOptionsWithUnits, TUnitConverterTypicalReturn } from '../types'

const CF_PER_GAL = 7.48052
const AF_PER_GAL = 325851.43
const M3_PER_GAL = 264.172
const ML_PER_GAL = 264172

// input unit is expected to be 'GAL'
const getConvertedFlowMeterCummulativeValue = (
  outputUnit: TVolumeUnit,
  flowMeterAmount: number | null = null,
) => {
  const shouldConvertGalToCF = outputUnit === 'CF'
  const shouldConvertGalToAF = outputUnit === 'AF'
  const shouldConvertGalToM3 = outputUnit === 'M3'
  const shouldConvertGalToML = outputUnit === 'ML'

  let convertedValue: number | null

  if (isNil(flowMeterAmount)) {
    convertedValue = null
  } else if (shouldConvertGalToCF) {
    convertedValue = flowMeterAmount / CF_PER_GAL
  } else if (shouldConvertGalToAF) {
    convertedValue = flowMeterAmount / AF_PER_GAL
  } else if (shouldConvertGalToM3) {
    convertedValue = flowMeterAmount / M3_PER_GAL
  } else if (shouldConvertGalToML) {
    convertedValue = flowMeterAmount / ML_PER_GAL
  } else {
    convertedValue = flowMeterAmount
  }

  return convertedValue
}

const getDefaultDecimalPlaces = (outputUnit: TVolumeUnit) => {
  if (outputUnit === 'CF') {
    return 2
  } else if (outputUnit === 'AF') {
    return 4
  } else if (outputUnit === 'M3') {
    return 2
  } else if (outputUnit === 'ML') {
    return 4
  } else {
    return 0
  }
}

const GPM_PER_CFS = 448.831
const GPM_PER_LS = 15.8503
const GPM_PER_M3S = 15850.3
const GPM_PER_M3H = 4.403

const getDefaultDecimalPlacesFlowRate = (outputUnit: TFlowRateUnit) => {
  if (outputUnit === 'CFS') {
    return 2
  } else if (outputUnit === 'LS') {
    return 2
  } else if (outputUnit === 'M3S') {
    return 2
  } else if (outputUnit === 'M3H') {
    return 2
  } else {
    return 0
  }
}

// input unit is expected to be 'GPM'
const getConvertedFlowMeterRateValue = (outputUnit: TFlowRateUnit, flowRateAmount: number | null = null) => {
  const shouldConvertGpmToCfs = outputUnit === 'CFS'
  const shouldConvertGpmToLs = outputUnit === 'LS'
  const shouldConvertGpmToM3s = outputUnit === 'M3S'
  const shouldConvertGpmToMh = outputUnit === 'M3H'

  let convertedValue: number | null

  if (isNil(flowRateAmount)) {
    convertedValue = null
  } else if (shouldConvertGpmToCfs) {
    convertedValue = flowRateAmount / GPM_PER_CFS
  } else if (shouldConvertGpmToLs) {
    convertedValue = flowRateAmount / GPM_PER_LS
  } else if (shouldConvertGpmToM3s) {
    convertedValue = flowRateAmount / GPM_PER_M3S
  } else if (shouldConvertGpmToMh) {
    convertedValue = flowRateAmount / GPM_PER_M3H
  } else {
    convertedValue = flowRateAmount
  }

  return convertedValue
}

export const convertCurrentFlowRateToGpm = (flowRateAmount: number) => {
  const currentFlowRate = userDetailsStore.getState().flowRate

  if (currentFlowRate === 'CFS') {
    return flowRateAmount * GPM_PER_CFS
  } else if (currentFlowRate === 'LS') {
    return flowRateAmount * GPM_PER_LS
  } else if (currentFlowRate === 'M3S') {
    return flowRateAmount * GPM_PER_M3S
  } else if (currentFlowRate === 'M3H') {
    return flowRateAmount * GPM_PER_M3H
  } else {
    return flowRateAmount
  }
}

const flowMeterFunctionMaker = (
  flowMeterAmount: number | null = null,
  {
    decimalPlaces,
    outputUnit = userDetailsStore.getState().volume,
  }: {
    decimalPlaces?: number
    outputUnit?: TVolumeUnit
  } = {},
) => {
  const defaultNumberOfDecimalPlaces = getDefaultDecimalPlaces(outputUnit)
  const decimalPlacesToUse = decimalPlaces ?? defaultNumberOfDecimalPlaces
  const convertedValue = getConvertedFlowMeterCummulativeValue(outputUnit, flowMeterAmount)

  let unitSymbol: string

  if (outputUnit === 'CF') {
    unitSymbol = translate.measurements.cubicFeet.unit()
  } else if (outputUnit === 'AF') {
    unitSymbol = translate.measurements.acreFeet.unit()
  } else if (outputUnit === 'M3') {
    unitSymbol = translate.measurements.cubicMeter.unit()
  } else if (outputUnit === 'ML') {
    unitSymbol = translate.measurements.megaLiters.unit()
  } else {
    unitSymbol = translate.measurements.gallons.unit()
  }

  return {
    categoryTitle: () => {
      return translate.phrases.templates('{{label}} ({{unitSymbol}})', {
        label: translate.phrases.banyanApp('Flow Meter'),
        unitSymbol,
      })
    },
    categoryTitleWithoutUnit: () => translate.phrases.banyanApp('Flow Meter'),
    defaultNumberOfDecimalPlaces: () => defaultNumberOfDecimalPlaces,
    suffix: () => unitSymbol,
    shortTitle: () => {
      return translate.phrases.banyanApp('Flow Meter')
    },
    title: () => {
      return translate.phrases.templates('{{label}} ({{unitSymbol}})', {
        label: translate.phrases.banyanApp('Flow Meter'),
        unitSymbol,
      })
    },
    titleWithoutUnit: () => {
      return translate.phrases.banyanApp('Flow Meter')
    },
    value: () => {
      if (isNil(convertedValue)) return null

      return +convertedValue?.toFixed(decimalPlacesToUse)
    },
    valueAsString: () => {
      if (outputUnit === 'AF') {
        return translate.measurements.acreFeet.value(convertedValue, decimalPlacesToUse)
      } else if (outputUnit === 'CF') {
        return translate.measurements.cubicFeet.value(convertedValue, decimalPlacesToUse)
      } else if (outputUnit === 'M3') {
        return translate.measurements.cubicMeter.value(convertedValue, decimalPlacesToUse)
      } else if (outputUnit === 'ML') {
        return translate.measurements.megaLiters.value(convertedValue, decimalPlacesToUse)
      } else {
        return translate.measurements.gallons.value(convertedValue, decimalPlacesToUse)
      }
    },
    valueWithSuffix: () => {
      if (outputUnit === 'AF') {
        return translate.measurements.acreFeet.valueWithUnit(convertedValue, decimalPlacesToUse)
      } else if (outputUnit === 'CF') {
        return translate.measurements.cubicFeet.valueWithUnit(convertedValue, decimalPlacesToUse)
      } else if (outputUnit === 'M3') {
        return translate.measurements.cubicMeter.valueWithUnit(convertedValue, decimalPlacesToUse)
      } else if (outputUnit === 'ML') {
        return translate.measurements.megaLiters.valueWithUnit(convertedValue, decimalPlacesToUse)
      } else {
        return translate.measurements.gallons.valueWithUnit(convertedValue, decimalPlacesToUse)
      }
    },
    valueWithNoRounding: () => convertedValue,
  }
}

const flowRateFunctionMaker = (
  flowRateAmount: number | null = null,
  {
    decimalPlaces,
    outputUnit = userDetailsStore.getState().flowRate,
  }: {
    decimalPlaces?: number
    outputUnit?: TFlowRateUnit
  } = {},
) => {
  const defaultNumberOfDecimalPlaces = getDefaultDecimalPlacesFlowRate(outputUnit)
  const decimalPlacesToUse = decimalPlaces ?? defaultNumberOfDecimalPlaces
  const convertedValue = getConvertedFlowMeterRateValue(outputUnit, flowRateAmount)

  let unitSymbol: string

  if (outputUnit === 'CFS') {
    unitSymbol = translate.measurements.cubicFeetPerSecond.unit()
  } else if (outputUnit === 'LS') {
    unitSymbol = translate.measurements.litersPerSecond.unit()
  } else if (outputUnit === 'M3S') {
    unitSymbol = translate.measurements.cubicMeterPerSecond.unit()
  } else if (outputUnit === 'M3H') {
    unitSymbol = translate.measurements.cubicMeterPerHour.unit()
  } else {
    unitSymbol = translate.measurements.gallonsPerMinute.unit()
  }

  return {
    categoryTitle: () => {
      return translate.phrases.templates('{{label}} ({{unitSymbol}})', {
        label: translate.phrases.banyanApp('Flow Meter Rate'),
        unitSymbol,
      })
    },
    categoryTitleWithoutUnit: () => translate.phrases.banyanApp('Flow Meter Rate'),
    defaultNumberOfDecimalPlaces: () => defaultNumberOfDecimalPlaces,
    suffix: () => unitSymbol,
    shortTitle: () => {
      return translate.phrases.banyanApp('Flow Meter Rate')
    },
    title: () => {
      return translate.phrases.templates('{{label}} ({{unitSymbol}})', {
        label: translate.phrases.banyanApp('Flow Meter Rate'),
        unitSymbol,
      })
    },
    titleWithoutUnit: () => {
      return translate.phrases.banyanApp('Flow Meter Rate')
    },
    value: () => {
      if (isNil(convertedValue)) return null

      return +convertedValue?.toFixed(decimalPlacesToUse)
    },
    valueAsString: () => {
      if (outputUnit === 'CFS') {
        return translate.measurements.cubicFeetPerSecond.value(convertedValue, decimalPlacesToUse)
      } else if (outputUnit === 'LS') {
        return translate.measurements.litersPerSecond.value(convertedValue, decimalPlacesToUse)
      } else if (outputUnit === 'M3S') {
        return translate.measurements.cubicMeterPerSecond.value(convertedValue, decimalPlacesToUse)
      } else if (outputUnit === 'M3H') {
        return translate.measurements.cubicMeterPerHour.value(convertedValue, decimalPlacesToUse)
      } else {
        return translate.measurements.gallonsPerMinute.value(convertedValue, decimalPlacesToUse)
      }
    },
    valueWithSuffix: () => {
      if (outputUnit === 'CFS') {
        return translate.measurements.cubicFeetPerSecond.valueWithUnit(convertedValue, decimalPlacesToUse)
      } else if (outputUnit === 'LS') {
        return translate.measurements.litersPerSecond.valueWithUnit(convertedValue, decimalPlacesToUse)
      } else if (outputUnit === 'M3S') {
        return translate.measurements.cubicMeterPerSecond.valueWithUnit(convertedValue, decimalPlacesToUse)
      } else if (outputUnit === 'M3H') {
        return translate.measurements.cubicMeterPerHour.valueWithUnit(convertedValue, decimalPlacesToUse)
      } else {
        return translate.measurements.gallonsPerMinute.valueWithUnit(convertedValue, decimalPlacesToUse)
      }
    },
    valueWithNoRounding: () => convertedValue,
  }
}

type TFlowMeterConversionFunction = (
  value?: number | null,
  options?: TUnitConverterOptionsWithUnits<TVolumeUnit>,
) => TUnitConverterTypicalReturn

type TFlowRateConversionFunction = (
  value?: number | null,
  options?: TUnitConverterOptionsWithUnits<TFlowRateUnit>,
) => TUnitConverterTypicalReturn

export const flowMeter: {
  flowMeter: TFlowMeterConversionFunction
  flowRate: TFlowRateConversionFunction
} = {
  flowMeter: flowMeterFunctionMaker,
  flowRate: flowRateFunctionMaker,
}
